import type { FacetMappings } from '#types/filters'

const normaliseKey = (value: string) => value.toLowerCase().replace(/_/g, ' ').trim()

/**
 * Resolves an agent colour intent (e.g. `Red`) to BloomReach facet values (`Red;FF0000`).
 * Values that already include a `;` or `%3B` (the URL-safe form used on `/search`) are
 * treated as mapped and returned unchanged.
 * @param input Raw colour label from the agent, a mapped `Label;hex` value, or `Label%3Bhex`.
 * @param colorMapping BloomReach colour key → `Label;hex` map from GetFacets.
 * @returns One or more catalog facet values.
 * @category Utils
 */
export function resolveRefinementValues(input: string, colorMapping: Record<string, string>): string[] {
  if (input.includes(';') || input.includes('%3B')) return [input]

  const normalisedInput = normaliseKey(input)
  const entries = Object.entries(colorMapping)

  const exactAndCompound = entries.filter(([key]) => {
    const normalisedKey = normaliseKey(key)
    if (normalisedKey === normalisedInput) return true
    return normalisedKey.split('/').some((part) => part.trim() === normalisedInput)
  })
  if (exactAndCompound.length > 0)
    return exactAndCompound.map(([, value]) => value)

  const partialMatches = entries.filter(([key]) => {
    const normalisedKey = normaliseKey(key)
    return normalisedKey.includes(normalisedInput) || normalisedInput.includes(normalisedKey)
  })
  if (partialMatches.length > 0)
    return partialMatches.map(([, value]) => value)

  return [input]
}

/**
 * Maps agent colour filters onto BloomReach facet values using GetFacets mappings.
 * Non-colour filters are copied through. A string colour value is treated as a single selection.
 * @param filters Agent or URL filter record, e.g. `{ colorFamily: ['Red'] }`.
 * @param mappings Site facet mappings (`colorFacetId` + `colorMapping`).
 * @returns A new filter record, or `undefined` when `filters` is empty.
 * @category Utils
 */
export function mapColorFilters(
  filters: Record<string, any> | undefined,
  mappings: FacetMappings,
): Record<string, any> | undefined {
  if (!filters) return undefined

  const mapped = { ...filters }
  const { colorFacetId, colorMapping } = mappings
  const colorValues = colorFacetId ? mapped[colorFacetId] : undefined
  if (colorValues == null) return mapped

  const list = Array.isArray(colorValues) ? colorValues : [colorValues]
  mapped[colorFacetId] = list.flatMap((value: string) => resolveRefinementValues(String(value), colorMapping))
  return mapped
}
